(ns frontend.updates.entity-scope
  (:require
   [common.endpoints.updates.feed-keys :as feed-keys]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.updates :as updates]
   [frontend.utils :as fu]))

(defn ->stream-id
  ([entity-id]
   (->stream-id entity-id nil))
  ([entity-id instance-id]
   (let [instance-part (when instance-id
                         (cond
                           (keyword? instance-id) (name instance-id)
                           :else (str instance-id)))]
     (str "updates.entity-scope/"
          entity-id
          (when instance-part
            (str ":" instance-part))))))

(defn open!
  [{:keys [entity-id scope instance-id on-message on-open on-error]}]
  {:pre [(int? entity-id)
         (ifn? on-message)]}
  (updates/open!
   {:stream-id  (->stream-id entity-id instance-id)
    :feed-key   feed-keys/entity-scope
    :request    (cond-> {:root-entity-id entity-id}
                  scope
                  (assoc :scope scope))
    :connection (cond-> {}
                  instance-id
                  (assoc :instance-id (str instance-id)))
    :on-message on-message
    :on-open    on-open
    :on-error   on-error}))

(defn close!
  ([entity-id]
   (close! entity-id nil))
  ([entity-id instance-id]
   (updates/close! (->stream-id entity-id instance-id))))

(def ^:private fetch-once-instance-id
  :entity-scope-fetch-once)

;; The subscribe RPC reply wraps the initial snapshot as {:body {:initial-message
;; {:ops [...]}}}, two levels deeper than the :ops upserts-in reads - both the
;; :body wrapper (see frontend.updates' own send-subscribe!) and the
;; :initial-message wrapper (the initial-snapshot envelope) need unwrapping.
;; Mirrors the same workaround already used by
;; frontend.view.entity-viewers.utils/reset-state! and
;; ui.data.view-entities.event for this shape mismatch.
(defn unwrap-initial-message
  [payload]
  (let [body (if (and (map? payload)
                      (map? (:body payload)))
               (:body payload)
               payload)]
    (:initial-message body)))

(defn upserts-in
  [message]
  (->> (:ops message)
       (filter (fn [op] (= :upsert (:op op))))
       (mapcat :entities)
       (remove nil?)))

(defn merge-upserts!
  "Merges an entity-scope message's upserted entities into `@session/entities`.
   Same merge `frontend.view.entity-viewers.utils` performs."
  [message]
  (let [upserts (upserts-in message)]
    (when (seq upserts)
      (fu/manage-entities session/reverse-lookup session/session upserts))
    upserts))

(defn open-merging!
  "Opens a live entity-scope subscription whose only job is to keep
   `@session/entities` topped up with the root entity's on-demand graph (root +
   1-hop refs + a generic second ref-hop, permission-checked).

   For views that render entity types the bulk sync does not deliver - see
   ai/sync-model.md, which says to give the view its own subscription rather
   than widen `common.sync-model/new-grid-generic-sync-entity-types`.

   Unlike `fetch-once!` the subscription stays open, so entities created or
   edited while the view is mounted arrive without a reload. Close it with
   `close!` on unmount, passing the same `instance-id`.

   `on-entities` is called with the merged entities after every message,
   including the initial snapshot - a view that keeps its own loading flag can
   clear it there."
  [{:keys [entity-id instance-id on-entities on-error]}]
  {:pre [(int? entity-id)]}
  (let [handle! (fn [message]
                  (let [upserts (merge-upserts! message)]
                    (when (fn? on-entities)
                      (on-entities upserts))))]
    (open!
     {:entity-id   entity-id
      :instance-id instance-id
      :on-open     (fn [response]
                     (handle! (unwrap-initial-message response)))
      :on-message  handle!
      :on-error    on-error})))

(defn fetch-once!
  "Opens an entity-scope subscription just long enough to grab the initial
   snapshot, merges it into `@session/entities` (same merge
   `frontend.view.entity-viewers.utils/reset-state!` uses), then immediately
   closes the subscription - for one-off actions that need an entity's
   on-demand graph (root + 1-hop refs + a generic second ref-hop from each
   first-hop entity, permission-checked) without a live subscription, e.g. hydrating session
   before opening the entry-criteria modal on a stage-move the backend
   refused.

   `on-done` is called with the merged entities (a coll of entity-maps,
   possibly empty); `on-error` with the error, if the fetch itself fails."
  [{:keys [entity-id on-done on-error]}]
  {:pre [(int? entity-id)
         (ifn? on-done)]}
  (let [close-once! (fn [] (close! entity-id fetch-once-instance-id))]
    (open!
     {:entity-id entity-id
      :instance-id fetch-once-instance-id
      :on-open (fn [response]
                 (close-once!)
                 (on-done (merge-upserts! (unwrap-initial-message response))))
      :on-message (fn [_message] nil)
      :on-error (fn [error]
                  (close-once!)
                  (when (fn? on-error)
                    (on-error error)))})))

(defn root-entity
  "The entity with `entity-id` in `entities`, as the backend sent it, or nil."
  [entities entity-id]
  (cu/find-first-by :db/id entity-id entities))

(defn load-entity!
  "Loads one entity from the backend. It always fetches. It does not look in the
   session first, because a session copy can be missing, a stub or old, and the
   session storage is going away. Every UI loads its data from the backend. See
   \"Load Frontend Data From The Backend\" in CLAUDE.md.

   `on-loaded` is called with the entity exactly as the backend sent it, and
   with all entities of the fetch: the entity, the entities that it points at
   and the entities that point at it. Pass the entity on to the next step. Do
   not look it up again in the session.

   `on-error` is called with `{:error :fetch-failed :entity-id id :cause ...}`
   when the fetch fails, and with `{:error :not-found :entity-id id}` when the
   backend sends no entity with that id, for example because the account may
   not read it. The caller must stop its step then and say so. It must not
   continue with partial data.

   The fetch also merges the entities into the session, so code that still reads
   the session sees the same data."
  [{:keys [entity-id on-loaded on-error]}]
  {:pre [(int? entity-id)
         (ifn? on-loaded)
         (ifn? on-error)]}
  (fetch-once!
   {:entity-id entity-id
    :on-done   (fn [entities]
                 (if-let [entity (root-entity entities entity-id)]
                   (on-loaded entity entities)
                   (on-error {:error :not-found
                              :entity-id entity-id})))
    :on-error  (fn [error]
                 (on-error {:error :fetch-failed
                            :entity-id entity-id
                            :cause error}))}))

(defn hydrate!
  "Puts a new entity in the session, so that views that read `@session/entities`
   can use it. Then it calls `on-done` with no arguments.

   The bulk sync and the live-update channel do not send business records to a
   new-grid account. An entity that this client creates with a REST call (for
   example an uploaded attachment) does not reach the session. This function
   fetches the on-demand graph of the entity once. The graph includes the entities
   that point at the entity, so the owner arrives with it.

   `on-done` is always called, also when the fetch fails. The fetch is a
   best-effort refresh. It must not stop the success path of the caller. An
   old-grid account has the entity from the bulk sync, so `on-done` is called
   at once."
  [{:keys [entity-id on-done]}]
  {:pre [(ifn? on-done)]}
  (if (and (int? entity-id)
           (session/new-grid-2023?))
    (fetch-once! {:entity-id entity-id
                  :on-done   (fn [_entities] (on-done))
                  :on-error  (fn [_error] (on-done))})
    (on-done)))
